<script lang="ts">
  import type { Snippet } from 'svelte';
  import { tick } from 'svelte';

  /**
   * 確認ダイアログ（components.md §5 ConfirmDialog）。
   * `<dialog>` のモーダル表示、フォーカストラップ、Esc で閉じる（oncancel）、初期フォーカスは安全な側（キャンセル）。
   * キー入力は外へ伝えない（カードのショートカットを動かさない）。
   */
  interface Props {
    open: boolean;
    title: string;
    confirmLabel: string;
    cancelLabel: string;
    children?: Snippet;
    onconfirm: () => void;
    oncancel: () => void;
    testid?: string;
  }

  let {
    open,
    title,
    confirmLabel,
    cancelLabel,
    children,
    onconfirm,
    oncancel,
    testid = 'confirm-dialog',
  }: Props = $props();

  const uid = $props.id();
  let dialog: HTMLDialogElement | undefined = $state();
  let cancelButton: HTMLButtonElement | undefined = $state();
  let returnFocus: HTMLElement | null = null;

  $effect(() => {
    const el = dialog;
    if (!el) return;
    if (open && !el.open) {
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
      void tick().then(() => cancelButton?.focus());
    } else if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
      returnFocus?.focus({ preventScroll: true });
      returnFocus = null;
    }
  });

  function focusables(): HTMLElement[] {
    return dialog
      ? [...dialog.querySelectorAll<HTMLElement>('button, [href], input, select, textarea')].filter(
          (el) => !el.hasAttribute('disabled'),
        )
      : [];
  }

  function onkeydown(e: KeyboardEvent) {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      oncancel();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = focusables();
    const first = items[0];
    const last = items.at(-1);
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
</script>

<dialog
  bind:this={dialog}
  class="dialog"
  aria-modal="true"
  aria-labelledby="{uid}-title"
  data-testid={testid}
  {onkeydown}
  oncancel={(e) => {
    e.preventDefault();
    oncancel();
  }}
>
  <h2 id="{uid}-title">{title}</h2>
  {#if children}
    <div class="body">{@render children()}</div>
  {/if}
  <div class="actions">
    <button
      bind:this={cancelButton}
      class="btn"
      type="button"
      data-testid="{testid}-cancel"
      onclick={oncancel}>{cancelLabel}</button
    >
    <button class="btn btn-primary" type="button" data-testid="{testid}-confirm" onclick={onconfirm}
      >{confirmLabel}</button
    >
  </div>
</dialog>

<style>
  .dialog {
    max-width: min(32rem, calc(100vw - 2 * var(--space-4)));
    padding: var(--space-5);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-lg);
    background: var(--surface-raised);
    color: var(--text);
    box-shadow: var(--shadow-lift);
  }
  .dialog::backdrop {
    background: rgb(0 0 0 / 0.4);
  }
  h2 {
    margin: 0 0 var(--space-3);
  }
  .body {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-3);
    margin-top: var(--space-5);
  }
</style>
